import { APIProvider } from "@vis.gl/react-google-maps";
import {
  getIsStateRequired,
  getGroupedCountryOptionsByName,
} from "utils/stateRequired";
import { Button } from "components/DesignSystem/Button/Button";
import { Combobox } from "components/DesignSystem/Combobox/Combobox";
import { Label, TextInput } from "components/DesignSystem/TextInput/TextInput";
import { Form, Formik, FormikValues, getIn, useFormikContext } from "formik";
import { useToast } from "hooks/useToast";
import { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import {
  useCreateAutofillMutation,
  useGetAutofillQuery,
  useUpdateAutofillMutation,
} from "store/apis/autofill";
import { useGetCountriesDataQuery } from "store/apis/onboarding";
import { AddressAutofill } from "types/Models/addressAutofill";
import { BackendError } from "types/utils/error";
import { object, string } from "yup";
import { AddressAutocomplete } from "./AddressAutocomplete";

const API_KEY = process.env.PUBLIC_GOOGLE_MAP_KEY;

export const AddressComponents = ({
  formPrefix = "",
  required = true,
  groupId,
  entityId,
  selectedAddressId,
  onAddressSelect,
  label = "Address",
  onClearAddress,
  handleCreateManually,
  fieldPrefix = "",
  srollToSave,
  hideClear,
  allowedCountries = [],
  note = null,
  error,
  isDisabled = false,
  size = "small",
}: {
  formPrefix?: string;
  required?: boolean;
  groupId: string;
  entityId: string;
  selectedAddressId?: string;
  onAddressSelect: (address: AddressAutofill | null) => void;
  label?: string | ReactNode;
  onClearAddress?: () => void;
  handleCreateManually?: () => void;
  fieldPrefix?: string;
  size?: "small" | "regular";
  srollToSave?: boolean;
  hideClear?: boolean;
  allowedCountries?: string[];
  note?: ReactNode;
  error?: string;
  isDisabled?: boolean;
}) => {
  const { data: countries } = useGetCountriesDataQuery();

  const countryOptions = useMemo(
    () => getGroupedCountryOptionsByName(countries),
    [countries]
  );

  const [createManually, setCreateManually] = useState(false);
  const { successToast, alertToast } = useToast();
  const [editAddressId, setEditAddressId] = useState<string | null>(null);

  const [createAutofill, { isLoading: addingAutofill }] =
    useCreateAutofillMutation();
  const [editAutofill, { isLoading: isUpdating }] = useUpdateAutofillMutation();
  const { data: autofills = [] } = useGetAutofillQuery(
    {
      groupId,
      autofillKey: "addresses",
      entityId,
    },
    { skip: !groupId || !entityId }
  );
  const isLoading = isUpdating || addingAutofill;

  const savedAddresses = allowedCountries.length
    ? autofills.filter((address) => allowedCountries.includes(address.country))
    : autofills;

  const saveButtonRef = useRef<HTMLButtonElement>(null);
  const streetAddressKey = `${formPrefix}${fieldPrefix}street_address`;
  const cityKey = `${formPrefix}${fieldPrefix}city`;
  const countryKey = `${formPrefix}${fieldPrefix}country`;
  const zipcodeKey = `${formPrefix}${fieldPrefix}zipcode`;
  const streetLine2Key = `${formPrefix}${fieldPrefix}street_address_line_2`;
  const stateKey = `${formPrefix}${fieldPrefix}state`;

  const { values, setFieldValue } = useFormikContext<Record<string, any>>();

  const defaultAddress = autofills.find(
    (address) => address.uuid === selectedAddressId
  );

  const updateAddressFields = (address?: AddressAutofill) => {
    setFieldValue(cityKey, address?.city || "");
    setFieldValue(countryKey, address?.country || "");
    setFieldValue(stateKey, address?.state || "");
    setFieldValue(zipcodeKey, address?.zipcode || "");
    setFieldValue(streetAddressKey, address?.street_address || "");
    setFieldValue(streetLine2Key, address?.street_address_line_2 || "");
  };

  const updateLocalAddressFields = (
    setLocalFieldValue: (field: string, value: any) => void,
    address?: AddressAutofill
  ) => {
    setLocalFieldValue(cityKey, address?.city || "");
    setLocalFieldValue(countryKey, address?.country || "");
    setLocalFieldValue(stateKey, address?.state || "");
    setLocalFieldValue(zipcodeKey, address?.zipcode || "");
    setLocalFieldValue(streetAddressKey, address?.street_address || "");
    setLocalFieldValue(streetLine2Key, address?.street_address_line_2 || "");
  };

  const clearAddress = (
    setLocalFieldValue: (field: string, value: any) => void
  ) => {
    updateLocalAddressFields(setLocalFieldValue, defaultAddress);
    updateAddressFields(defaultAddress);
    setFieldValue(`${formPrefix}${fieldPrefix}uuid`, selectedAddressId);
    onClearAddress?.();
    setCreateManually(false);
    setEditAddressId(null);
  };

  const getAddressPayload = (values: Record<string, any>) => {
    return {
      street_address: values[streetAddressKey],
      street_address_line_2: values[streetLine2Key],
      city: values[cityKey],
      country: values[countryKey],
      state: values[stateKey],
      zipcode: values[zipcodeKey],
    };
  };

  const addAddress = async (values: FormikValues) => {
    try {
      const autofill = await createAutofill({
        fields: getAddressPayload(values),
        groupId,
        autofillKey: "addresses",
        entityId,
      }).unwrap();
      onAddressSelect(autofill as AddressAutofill);
      updateAddressFields(autofill as AddressAutofill);
      successToast({ message: "Autofill added successfully" });
      setCreateManually(false);
      setEditAddressId(null);
    } catch (error) {
      alertToast({ message: (error as BackendError).data?.error?.message });
    }
  };

  const editAddress = async (values: FormikValues) => {
    try {
      const autofill = await editAutofill({
        fields: getAddressPayload(values),
        groupId,
        autofillKey: "addresses",
        entityId,
        uuid: editAddressId!,
      }).unwrap();
      onAddressSelect(autofill as AddressAutofill);
      updateAddressFields(autofill as AddressAutofill);
      successToast({ message: "Autofill updated successfully" });
      setCreateManually(false);
      setEditAddressId(null);
    } catch (error) {
      alertToast({ message: (error as BackendError).data?.error?.message });
    }
  };

  const onCreateManually = () => {
    setCreateManually(true);
    handleCreateManually?.();
    setEditAddressId(null);
  };

  const onEditAddress = (
    addressId: string,
    setLocalFieldValue: (field: string, value: any) => void
  ) => {
    const selectedAddress = autofills.find(({ uuid }) => uuid === addressId);
    updateLocalAddressFields(setLocalFieldValue, selectedAddress);
    updateAddressFields(selectedAddress);
    setFieldValue(`${formPrefix}${fieldPrefix}uuid`, addressId);
    onCreateManually();
    setCreateManually(true);
    setEditAddressId(addressId);
  };

  useEffect(() => {
    if (srollToSave && saveButtonRef.current) {
      saveButtonRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [editAddressId, saveButtonRef.current, srollToSave, createManually]);

  return (
    <Formik
      validateOnChange
      validationSchema={object({
        [streetAddressKey]: string().required("Address Line 1 Required"),
        [cityKey]: string().required("City Required"),
        [countryKey]: string().required("Country Required"),
        [stateKey]: string().when(countryKey, {
          is: (country: string) => getIsStateRequired(countries, country),
          then: string().required("State Required"),
          otherwise: string().notRequired(),
        }),
        [zipcodeKey]: string().required("Zipcode Required"),
      })}
      onSubmit={editAddressId ? editAddress : addAddress}
      initialValues={{
        [streetAddressKey]: defaultAddress?.street_address || "",
        [streetLine2Key]: defaultAddress?.street_address_line_2 || "",
        [cityKey]: defaultAddress?.city || "",
        [countryKey]: defaultAddress?.country || "",
        [stateKey]: defaultAddress?.state || "",
        [zipcodeKey]: defaultAddress?.zipcode || "",
      }}
      enableReinitialize
      validateOnMount={false}
    >
      {({ submitForm, setFieldValue: setLocalFieldValue, values }) => {
        const selectedCountry = getIn(values, countryKey);
        const selectedState = getIn(values, stateKey);
        const isStateRequired = getIsStateRequired(countries, selectedCountry);
        return (
          <Form>
            <>
              {!createManually && (
                <APIProvider
                  apiKey={API_KEY || ""}
                  solutionChannel="GMP_devsite_samples_v3_rgmautocomplete"
                >
                  <div className="autocomplete-control">
                    <AddressAutocomplete
                      formPrefix={formPrefix}
                      availableCountriesData={countries || []}
                      required={required}
                      selectedAddressId={selectedAddressId}
                      savedAddresses={savedAddresses}
                      getSavedAddressLabel={(address) =>
                        address.autofill_string
                      }
                      onAddressSelect={onAddressSelect}
                      allowedCountries={allowedCountries}
                      onCreateManually={onCreateManually}
                      label={label}
                      field_prefix={fieldPrefix}
                      onEditAddress={(addressId: string) =>
                        onEditAddress(addressId, setLocalFieldValue)
                      }
                      hideClear={hideClear}
                      note={note}
                      error={error}
                      isDisabled={isDisabled}
                      size={size}
                    />
                  </div>
                </APIProvider>
              )}
              {createManually && (
                <div>
                  <Label>{label}</Label>
                  <div className="t-shadow-light-30 t-space-y-4 t-border t-border-solid t-border-neutral-0 t-rounded-lg t-p-4">
                    <div className="t-text-subtitle-sm t-text-text-100">
                      {editAddressId ? "Edit Address" : "Add Address"}
                    </div>
                    <div className="t-relative t-w-full">
                      <TextInput
                        block
                        required={required}
                        label="Address Line 1"
                        placeholder="Street address"
                        name={streetAddressKey}
                        autoComplete="new-password"
                        customSize="small"
                      />
                    </div>
                    <div>
                      <TextInput
                        block
                        label="Address Line 2"
                        placeholder="Apartment, suite, building, floor etc."
                        name={streetLine2Key}
                        autoComplete="new-password"
                        customSize="small"
                      />
                    </div>
                    <div>
                      <Combobox
                        required={required}
                        name={countryKey}
                        menuPosition="fixed"
                        menuPortalTarget={document.body}
                        withForm
                        label="Country"
                        placeholder="Select Country"
                        size="small"
                        components={{
                          GroupHeading: ({ children }) => (
                            <div className="t-text-neutral-40 t-text-body-sm t-font-light t-pl-3 t-pr-3 t-pb-1 t-pt-3">
                              {children}
                            </div>
                          ),
                        }}
                        options={countryOptions}
                        value={
                          selectedCountry
                            ? {
                                label: selectedCountry,
                                value: selectedCountry,
                              }
                            : null
                        }
                      />
                    </div>
                    <div>
                      <Combobox
                        name={stateKey}
                        required={isStateRequired}
                        withForm
                        menuPortalTarget={document.body}
                        label="State"
                        placeholder="Select State"
                        menuPosition="fixed"
                        size="small"
                        options={
                          countries
                            ?.find((c) => c.name === selectedCountry)
                            ?.states?.map((state) => ({
                              value: state.name,
                              label: state.name,
                            }))!
                        }
                        value={
                          selectedState
                            ? { label: selectedState, value: selectedState }
                            : null
                        }
                      />
                    </div>
                    <div className="t-flex t-gap-6 t-w-full sm:t-flex-col sm:t-gap-4">
                      <div className="t-flex-1 t-min-w-0">
                        <TextInput
                          required={required}
                          block
                          label="City"
                          placeholder="Enter City"
                          name={cityKey}
                          autoComplete="new-password"
                          customSize="small"
                        />
                      </div>
                      <div className="t-flex-1 t-min-w-0">
                        <TextInput
                          required={required}
                          block
                          placeholder="Enter Zip, postal or pin code"
                          label="Zip, postal or pin code"
                          name={zipcodeKey}
                          autoComplete="new-password"
                          customSize="small"
                        />
                      </div>
                    </div>
                    <div className="t-flex t-justify-end t-gap-4">
                      <Button
                        customType="ghost"
                        type="button"
                        onClick={() => clearAddress(setLocalFieldValue)}
                        size="small"
                      >
                        Cancel
                      </Button>
                      <Button
                        customType="primary"
                        type="submit"
                        disabled={isLoading}
                        isLoading={isLoading}
                        size="small"
                        ref={saveButtonRef}
                        onClick={submitForm}
                      >
                        Save address
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </>
          </Form>
        );
      }}
    </Formik>
  );
};
